<template>
  <div class="video-model-seo-page">
    <div class="veo-first-screen">
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <router-link :to="localizedPath('/')">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
        <span>&rsaquo;</span>
        <router-link :to="localizedPath('/models')">{{ $t('footer.aiVideos') }}</router-link>
        <span>&rsaquo;</span>
        <span>Veo 3.1</span>
      </nav>

      <section class="veo-hero">
        <div class="veo-hero-copy">
          <span class="veo-eyebrow">GOOGLE DEEPMIND · VEO 3.1</span>
          <h1>{{ $t('seo.videoModels.veo31.heroHeadline') }}</h1>
          <p>{{ $t('seo.videoModels.veo31.heroIntro') }}</p>
          <div class="veo-badges" aria-label="Veo 3.1 capabilities">
            <span v-for="badge in badges" :key="badge">{{ badge }}</span>
          </div>
        </div>

        <div class="veo-generator-card">
          <div class="veo-generator-tabs" role="tablist" :aria-label="$t('seo.videoModels.modeLabel')">
            <button type="button" :class="{ active: mode === 'text' }" @click="mode = 'text'">{{ $t('seo.videoModels.textToVideo') }}</button>
            <button type="button" :class="{ active: mode === 'image' }" @click="mode = 'image'">{{ $t('seo.videoModels.imageToVideo') }}</button>
          </div>
          <div class="veo-generator-heading">
            <div>
              <span>{{ $t('seo.videoModels.promptLabel') }}</span>
              <strong>Veo 3.1</strong>
            </div>
            <span class="veo-credit-pill">{{ model ? model.credits : 30 }} credits</span>
          </div>
          <p v-if="mode === 'image'" class="veo-upload-note">{{ $t('seo.videoModels.imageUploadNote') }}</p>
          <textarea
            ref="promptInput"
            v-model="prompt"
            rows="5"
            maxlength="2000"
            :placeholder="$t('seo.videoModels.promptPlaceholder')"
            @keydown.enter.ctrl="goGenerate"
            @keydown.enter.meta="goGenerate"
          ></textarea>
          <div class="veo-settings" aria-label="Veo 3.1 generation settings">
            <span>16:9</span>
            <span>8s</span>
            <span>{{ $t('seo.videoModels.nativeAudio') }}</span>
            <span>1080p</span>
          </div>
          <div class="veo-generator-footer">
            <span class="veo-shortcut">Ctrl / ⌘ + Enter</span>
            <div>
              <span class="veo-count">{{ prompt.length }}/2000</span>
              <button type="button" class="veo-generate" :disabled="!prompt.trim()" @click="goGenerate">
                <span>{{ $t('home.generate') }}</span>
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <main class="veo-content-container">
      <veo31-seo-content
        :explore-models="exploreModels"
        :feature-links="featureLinks"
        :demo-sections="demoSections"
        :use-cases="useCases"
        :comparisons="comparisons"
        :pros="pros"
        :limits="limits"
        :how-to-steps="howToSteps"
        :faqs="faqs"
        @use-prompt="usePrompt"
        @back-to-generator="scrollToGenerator"
      />
    </main>
  </div>
</template>

<script>
var Veo31SeoContent = require('@/components/seo/Veo31SeoContent.vue').default

module.exports = {
  name: 'VideoModelSeo',
  components: { Veo31SeoContent: Veo31SeoContent },
  data: function () {
    return {
      prompt: '',
      mode: 'text'
    }
  },
  metaInfo: function () {
    var title = this.$t('seo.videoModels.veo31.title')
    var description = this.$t('seo.videoModels.veo31.description')
    var canonicalUrl = 'https://www.miralvis.com' + this.$route.path
    var faqEntities = this.faqs.map(function (faq) {
      return { '@type': 'Question', name: faq.q, acceptedAnswer: { '@type': 'Answer', text: faq.a } }
    })
    return {
      title: title,
      meta: [
        { vmid: 'description', name: 'description', content: description },
        { vmid: 'og:title', property: 'og:title', content: title },
        { vmid: 'og:description', property: 'og:description', content: description },
        { vmid: 'twitter:title', name: 'twitter:title', content: title },
        { vmid: 'twitter:description', name: 'twitter:description', content: description }
      ],
      script: [
        { vmid: 'faq-jsonld', type: 'application/ld+json', json: { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqEntities } },
        { vmid: 'breadcrumb-jsonld', type: 'application/ld+json', json: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.miralvis.com/' },
          { '@type': 'ListItem', position: 2, name: 'AI Video Models', item: 'https://www.miralvis.com/models' },
          { '@type': 'ListItem', position: 3, name: 'Veo 3.1', item: canonicalUrl }
        ] } }
      ]
    }
  },
  computed: {
    model: function () { return this.$store.getters.getModelById('veo-3-1') },
    badges: function () { return this.translationArray('badges') },
    exploreModels: function () { return this.translationArray('exploreModels') },
    featureLinks: function () { return this.translationArray('featureLinks') },
    demoSections: function () { return this.translationArray('demoSections') },
    useCases: function () { return this.translationArray('useCases') },
    comparisons: function () { return this.translationArray('comparisons') },
    pros: function () { return this.translationArray('pros') },
    limits: function () { return this.translationArray('limits') },
    howToSteps: function () { return this.translationArray('howToSteps') },
    faqs: function () { return this.translationArray('faqs') }
  },
  methods: {
    // 读取 Veo 3.1 页面中的数组型多语言配置，缺失时返回空数组。
    translationArray: function (field) {
      var key = 'seo.videoModels.veo31.' + field
      var value = this.$te(key) ? this.$t(key) : []
      return Array.isArray(value) ? value : []
    },
    // 为站内链接保留当前语言前缀。
    localizedPath: function (path) {
      var lang = this.$route.params.lang
      return (lang ? '/' + lang : '') + (path === '/' ? '/' : path)
    },
    // 将提示词带入现有 Veo 3.1 生成页，不改变原有生成流程。
    goGenerate: function () {
      if (!this.prompt.trim()) return
      this.$router.push({ path: this.localizedPath('/models/veo-3-1'), query: { prompt: this.prompt.trim() } })
    },
    // 使用正文示例提示词并返回首屏输入区域。
    usePrompt: function (prompt) {
      this.prompt = prompt
      this.scrollToGenerator()
      var self = this
      this.$nextTick(function () {
        if (self.$refs.promptInput) self.$refs.promptInput.focus()
      })
    },
    // 平滑返回页面顶部的 Veo 3.1 输入区域。
    scrollToGenerator: function () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.video-model-seo-page { padding-bottom: 90px; overflow: hidden; background: #fff; color: #17191f; }
.veo-first-screen { width: 100%; box-sizing: border-box; margin-bottom: 94px; padding: 28px max(28px, calc((100vw - 1120px) / 2 + 28px)) 48px; background: radial-gradient(circle at 82% 5%, rgba(155, 231, 205, .62), transparent 31%), radial-gradient(circle at 14% 18%, rgba(213, 217, 255, .72), transparent 34%), linear-gradient(145deg, #f7f8fa, #f0f3f5); }
.breadcrumb { display: flex; width: 100%; max-width: 1064px; align-items: center; gap: 9px; margin: 0 auto 28px; color: #a1a6b0; font-size: 13px; flex-wrap: wrap; }
.breadcrumb a { color: #707681; text-decoration: none; }
.breadcrumb a:hover { color: #059669; }
.veo-hero { width: 100%; max-width: 1064px; margin: 0 auto; }
.veo-hero-copy { max-width: 900px; margin: 0 auto; text-align: center; }
.veo-eyebrow { display: inline-block; color: #059669; font-size: 12px; font-weight: 800; letter-spacing: .12em; }
.veo-hero h1 { max-width: 920px; margin: 14px auto 18px; font-size: clamp(40px, 5.2vw, 64px); line-height: 1.05; letter-spacing: -.048em; }
.veo-hero-copy > p { max-width: 820px; margin: 0 auto; color: #626873; font-size: 17px; line-height: 1.75; }
.veo-badges { display: flex; justify-content: center; gap: 8px; margin-top: 20px; flex-wrap: wrap; }
.veo-badges span { padding: 8px 12px; border: 1px solid rgba(221, 225, 230, .9); border-radius: 999px; background: rgba(255,255,255,.68); color: #4f5560; font-size: 12px; font-weight: 650; }
.veo-generator-card { width: 100%; box-sizing: border-box; margin-top: 34px; padding: 16px 22px 17px; border: 1px solid rgba(222,226,231,.94); border-radius: 20px; background: rgba(255,255,255,.94); box-shadow: 0 18px 54px rgba(26,31,41,.08); backdrop-filter: blur(12px); }
.veo-generator-tabs { display: flex; gap: 4px; margin-bottom: 15px; padding-bottom: 14px; border-bottom: 1px solid #eef0f2; }
.veo-generator-tabs button { padding: 8px 12px; border: 0; border-radius: 8px; background: transparent; color: #858b95; cursor: pointer; font-size: 12px; font-weight: 700; }
.veo-generator-tabs button.active { background: #edf8f4; color: #047857; }
.veo-generator-heading, .veo-generator-footer, .veo-generator-heading > div, .veo-generator-footer > div { display: flex; align-items: center; justify-content: space-between; }
.veo-generator-heading > div { gap: 10px; }
.veo-generator-heading span { color: #9399a3; font-size: 12px; }
.veo-generator-heading strong { font-size: 13px; }
.veo-generator-heading .veo-credit-pill { padding: 6px 10px; border-radius: 999px; background: #f0faf6; color: #047857; font-size: 11px; font-weight: 750; }
.veo-upload-note { margin: 14px 0 0; padding: 11px 13px; border-radius: 8px; background: #f7f8fa; color: #777d87; font-size: 12px; }
.veo-generator-card textarea { width: 100%; min-height: 112px; box-sizing: border-box; padding: 18px 0 10px; border: 0; outline: 0; resize: none; background: transparent; color: #20232a; font: 15px/1.65 inherit; }
.veo-generator-card textarea::placeholder { color: #b1b5bd; }
.veo-settings { display: flex; gap: 8px; margin-bottom: 15px; flex-wrap: wrap; }
.veo-settings span { padding: 7px 10px; border: 1px solid #e1e4e7; border-radius: 8px; background: #fafbfb; color: #626873; font-size: 11px; font-weight: 650; }
.veo-generator-footer { padding-top: 13px; border-top: 1px solid #eef0f2; }
.veo-generator-footer > div { gap: 14px; }
.veo-shortcut, .veo-count { color: #adb1b8; font-size: 11px; }
.veo-generate { display: inline-flex; align-items: center; gap: 9px; padding: 11px 18px; border: 0; border-radius: 10px; background: #111318; color: #fff; cursor: pointer; font-size: 13px; font-weight: 750; transition: transform .18s, background .18s; }
.veo-generate:hover:not(:disabled) { transform: translateY(-1px); background: #059669; }
.veo-generate:disabled { background: #d8dbe0; cursor: not-allowed; }
.veo-content-container { width: 100%; max-width: 1120px; box-sizing: border-box; margin: 0 auto; padding: 0 28px; }
@media (max-width: 640px) {
  .video-model-seo-page { padding-bottom: 60px; }
  .veo-first-screen { margin-bottom: 70px; padding: 20px 18px 30px; }
  .breadcrumb { margin-bottom: 8px; }
  .veo-hero { padding-top: 28px; }
  .veo-hero h1 { font-size: 38px; }
  .veo-hero-copy > p { font-size: 15px; }
  .veo-generator-card { margin-top: 28px; padding: 14px 16px 16px; }
  .veo-generator-tabs button { flex: 1; }
  .veo-generator-footer { align-items: flex-end; gap: 14px; }
  .veo-shortcut { display: none; }
  .veo-generator-footer > div { width: 100%; flex-wrap: wrap; }
  .veo-count { margin-left: auto; }
  .veo-generate { width: 100%; justify-content: center; }
  .veo-content-container { padding: 0 18px; }
}
</style>
